<template>
  <div>
    <v-dialog
      v-model="show"
      max-width="50vw"
    >
      <v-card>
        <v-card-title>
          Departments history
          <v-spacer></v-spacer>
          <v-btn icon @click.prevent="show = !show">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-data-table
            dense
            :headers="headers"
            :items="departmentsHistory"
            :key="`update-table-${updateKey}`"
          >
            <!-- From date -->
            <template v-slot:[`item.From_Date`]='{ item, index }'>
              <DatePicker
                v-if="editMode && currentRowIndex === index"
                v-model="currentItem.From_Date"
                dense
                :min="minD"
                :max="maxD"
              />
              <span v-else>
                {{ item.From_Date }}
              </span>
            </template>
            <!-- /From date -->
            <!-- Department Name -->
            <template v-slot:[`item.Dep_Name`]='{ item, index }'>
              <v-select
                v-if="editMode && currentRowIndex === index"
                dense
                hide-details="auto"
                v-model="currentItem.Dep_Name"
                :items="departments"
                item-text="Dep_Name"
                item-value="Dep_Name"
              ></v-select>
              <span v-else>{{ item.Dep_Name }}</span>
            </template>
            <!-- /Department Name -->
            <!-- Edit -->
            <template v-slot:[`item.edit`]='{ item, index }'>
              <!-- Edit/save button -->
              <v-tooltip bottom :color="editMode && currentRowIndex === index ? 'success' : 'primary'">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :disabled="(editMode && currentRowIndex !== index) || (!editMode && addNewMode) || removeDialog"
                    small
                    v-bind="attrs"
                    v-on="on"
                    icon
                    :color="editMode && currentRowIndex === index ? 'success' : 'primary'"
                    @click.prevent="onEditClick(item, index)"
                  >
                    <v-icon>{{ editMode && currentRowIndex === index ? 'mdi-check-circle' : 'mdi-pencil-circle' }}</v-icon>
                  </v-btn>
                </template>
                <span>{{ editMode ? 'Save' : 'Edit' }}</span>
              </v-tooltip>
              <!-- /Edit/save button -->
              <!-- Delete button -->
              <v-tooltip bottom color="red darken-2" v-if="currentRowIndex !== index && (projectID || !departmentsHistory[0])">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    small
                    :disabled="editMode || addNewMode || removeDialog"
                    v-bind="attrs"
                    v-on="on"
                    icon color="red darken-2"
                    @click.prevent="openDeleteDialog(item)"
                  >
                    <v-icon>mdi-delete-circle</v-icon>
                  </v-btn>
                </template>
                <span>Delete</span>
              </v-tooltip>
              <!-- /Delete button -->
              <!-- Cancel button -->
              <v-tooltip bottom color="primary" v-if="editMode && index === currentRowIndex">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    small
                    v-bind="attrs"
                    v-on="on"
                    icon color="primary"
                    @click.prevent="onCancel"
                  >
                    <v-icon>mdi-close-circle</v-icon>
                  </v-btn>
                </template>
                <span>Cancel</span>
              </v-tooltip>
              <!-- /Cancel button -->
            </template>
            <!-- /Edit -->
            <!-- Add new row -->
            <template v-slot:[`body.append`] v-if="!notFromProjectsModule">
              <!-- Departments -->
              <td
                class="text-center px-2 text-no-wrap"
                :class="{'v-data-table__divider': addNewMode}"
              >
                <v-select
                  v-if="addNewMode"
                  dense
                  hide-details="auto"
                  v-model="currentItem.Department_ID"
                  :items="departments"
                  item-text="Dep_Name"
                  item-value="Dep_ID"
                ></v-select>
              </td>
              <!-- /Departments -->
              <!-- From date -->
              <td
                class="text-center px-2 text-no-wrap"
                :class="{'v-data-table__divider': addNewMode}"
              >
                <DatePicker
                  v-if="addNewMode"
                  v-model="currentItem.From_Date"
                  :min="minD"
                  dense
                />
              </td>
              <!-- /From date -->
              <!-- Add/save/cancel -->
              <td class="text-center px-2 text-no-wrap">
                <!-- Add/save -->
                <v-tooltip
                  bottom
                  color="success"
                  v-if="!notFromProjectsModule && (projectID || !departmentsHistory[0])"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      small
                      icon
                      :disabled="!canSave && currentItem !== null"
                      color="success"
                      v-bind="attrs"
                      v-on="on"
                      @click.prevent="addNew"
                    >
                      <v-icon>{{ addNewMode ? 'mdi-check-circle' : 'mdi-plus-circle' }}</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ addNewMode ? 'Save' : 'Add new' }}</span>
                </v-tooltip>
                <!-- /Add/save -->
                <!-- Cancel -->
                <v-tooltip bottom color="primary" v-if="addNewMode">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      small
                      icon
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      @click.prevent="onCancel"
                    >
                      <v-icon>mdi-close-circle</v-icon>
                    </v-btn>
                  </template>
                  <span>Cancel</span>
                </v-tooltip>
              </td>
              <!-- /Cancel -->
            </template>
            <!-- /Add new row -->
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
    <ConfirmOrDeniedDialog
      v-if="removeDialog"
      v-model="removeDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      :message="'You can\'t restore data after delete'"
      @onDeleteClick="deleteDepartmentHistory"
      @onCancelClick="removeDialog = false; currentItem = null"
      @onCloseClick="removeDialog = false; currentItem = null"
    />
  </div>
</template>
<script>
import { getApiErrorMessage } from '@/utils/apiError'
import { formatLocalDate } from '@/utils/dateUtils'

export default {
  name: 'ProjectDepartment',
  components: {
    DatePicker: () => import('@/components/global/DatePicker.vue'),
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    value: {
      required: false,
      default: false
    },
    projectID: {
      required: false,
      default: null
    },
    minDate: {
      required: false,
      default: null
    },
    notFromProjectsModule: {
      required: false,
      default: false
    },
    infoForCreatingProjectDepHistory: {
      required: false,
      type: Array
    }
  },
  data () {
    return {
      addNewMode: false,
      editMode: false,
      departmentsHistory: [],
      updateKey: 0,
      currentItem: null,
      currentRowIndex: null,
      removeDialog: false
    }
  },
  mounted () {
    if (this.projectID) {
      this.getDepartmentsHistory()
    }
    if (!this.projectID) {
      this.departmentsHistory = this.infoForCreatingProjectDepHistory
    }
  },
  computed: {
    headers () {
      const headers = [
        {
          text: 'Department',
          value: 'Dep_Name',
          divider: !this.notFromProjectsModule,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'From Date',
          value: 'From_Date',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        }
      ]
      if (!this.notFromProjectsModule) {
        headers.push({
          text: '#',
          value: 'edit',
          align: 'center',
          class: 'px-2',
          cellClass: 'px-2 text-no-wrap'
        })
      }
      return headers
    },
    user () {
      return this.$store.getters.getUser
    },
    departments () {
      return this.$store.getters.getDepartments
    },
    show: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    canSave () {
      return this.currentItem ? this.currentItem.From_Date && this.currentItem.Department_ID : false
    },
    currentIndexInArray () {
      return this.currentItem && this.currentItem.ID
        ? this.departmentsHistory.findIndex(item => item.ID === this.currentItem.ID)
        : this.departmentsHistory.length
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    minD () {
      return this.currentIndexInArray > -1 && this.currentIndexInArray && this.projectID
        ? this.departmentsHistory[this.currentIndexInArray - 1].From_Date
        : this.minDate
    },
    maxD () {
      return this.currentIndexInArray > -1 && this.departmentsHistory[this.currentIndexInArray + 1]
        ? this.departmentsHistory[this.currentIndexInArray + 1].From_Date
        : null
    }
  },
  methods: {
    addNew () {
      if (this.departmentsHistory.length) {
        this.currentRowIndex = this.departmentsHistory.length
      } else {
        this.currentRowIndex = 0
      }
      if (this.addNewMode) {
        if (!this.projectID) {
          const foundDep = this.departments.find(el => el.Dep_ID === this.currentItem.Department_ID)
          if (!foundDep) {
            this.$store.dispatch('setError', { errorMsg: 'Department not found' })
            return
          }

          const infoForCreatingHistory = {
            Dep_Name: foundDep.Dep_Name,
            From_Date: this.currentItem.From_Date
          }
          this.$emit('updateProjectCreatingDepInfo', infoForCreatingHistory)
          this.show = false
          return
        }
        this.postNewDepartmentHistory()
      } else {
        this.currentItem = {
          ID: null,
          Project_ID: this.projectID,
          From_Date: this.minD,
          Department_ID: null
        }
      }
      this.addNewMode = !this.addNewMode
    },
    onCancel () {
      this.editMode = false
      this.addNewMode = false
      this.currentItem = null
      this.currentRowIndex = null
    },
    openDeleteDialog (item) {
      this.currentItem = Object.assign({}, item)
      this.removeDialog = true
    },
    onEditClick (item, index) {
      if (this.editMode) {
        if (!this.projectID) {
          const infoForCreatingHistory = {
            Dep_Name: this.currentItem.Dep_Name,
            From_Date: this.currentItem.From_Date
          }
          this.$emit('updateProjectCreatingDepInfo', infoForCreatingHistory)
          this.departmentsHistory = this.infoForCreatingProjectDepHistory
          this.show = false
          return
        }
        this.updateDepartmentHistory()
      } else {
        this.currentItem = Object.assign({}, item)
        this.currentRowIndex = index
      }
      this.editMode = !this.editMode
    },
    getDepartmentsHistory () {
      this.axios.get(`/api/project_departments_history/${this.projectID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        if (response.data && response.data.length) {
          response.data.forEach(item => {
            item.From_Date = formatLocalDate(item.From_Date)
          })
          this.departmentsHistory = response.data
        }
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    postNewDepartmentHistory () {
      this.axios.post('/api/project_departments_history', this.currentItem, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        response.data.forEach(item => {
          item.From_Date = formatLocalDate(item.From_Date)
        })
        this.departmentsHistory = response.data
        this.$store.dispatch('setSuccess', { successMsg: 'Department successfully added' })
        this.$emit('departmentHistoryUpdated')
        this.updateKey++
        this.currentItem = null
        this.currentRowIndex = null
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    deleteDepartmentHistory () {
      let previousDepartment
      const sortedDepartmentsHistory = [...this.departmentsHistory].sort((a, b) => (a.From_Date.toString()).localeCompare(b.From_Date)).reverse()
      const lastAddedDepartmentID = sortedDepartmentsHistory[0]?.ID

      if (lastAddedDepartmentID === this.currentItem.ID && this.departmentsHistory.length > 1) {
        const targetDepName = sortedDepartmentsHistory[1]?.Dep_Name
        previousDepartment = this.departments.find(el => el.Dep_Name === targetDepName)
        if (!previousDepartment) {
          this.$store.dispatch('setError', { errorMsg: 'Previous department not found in the departments list' })
          return
        }
      } else if (this.departmentsHistory.length === 1) {
        this.removeDialog = false
        this.currentItem = null
        this.$store.dispatch('setError', { errorMsg: 'You cannot delete the primary department' })
        return
      }

      let query = `/api/project_departments_history/${this.currentItem.ID}`
      if (previousDepartment) {
        query += `/${previousDepartment.Dep_ID}`
      }
      this.axios.delete(query, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        response.data.forEach(item => {
          item.From_Date = formatLocalDate(item.From_Date)
        })
        this.departmentsHistory = response.data
        if (previousDepartment) {
          this.$emit('departmentHistoryUpdated')
        }
        this.$store.dispatch('setSuccess', { successMsg: 'Department successfully deleted' })
        this.removeDialog = false
        this.updateKey++
        this.currentItem = null
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    updateDepartmentHistory () {
      let changeProjectDepartment = false
      const foundDep = this.departments.find(el => el.Dep_Name === this.currentItem.Dep_Name)
      if (!foundDep) {
        this.$store.dispatch('setError', { errorMsg: 'Selected department not found' })
        return
      }

      const departmentID = foundDep.Dep_ID
      const sortedDepartmentsHistory = [...this.departmentsHistory].sort((a, b) => (a.From_Date.toString()).localeCompare(b.From_Date)).reverse()
      const lastAddedDepartment = sortedDepartmentsHistory[0]

      if (lastAddedDepartment && lastAddedDepartment.ID === this.currentItem.ID && lastAddedDepartment.Dep_Name !== this.currentItem.Dep_Name) {
        changeProjectDepartment = true
      }

      this.axios.put(`/api/project_departments_history/${this.currentItem.ID}`,
        {
          Project_ID: this.currentItem.Project_ID,
          From_Date: this.currentItem.From_Date,
          Department_ID: departmentID,
          ChangeProjectDepartment: changeProjectDepartment || null
        },
        {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(response => {
        response.data.forEach(item => {
          item.From_Date = formatLocalDate(item.From_Date)
        })
        this.departmentsHistory = response.data

        if (changeProjectDepartment) {
          this.$emit('departmentHistoryUpdated')
        }

        this.$store.dispatch('setSuccess', { successMsg: 'Department successfully updated' })
        this.updateKey++
        this.currentItem = null
        this.currentRowIndex = null
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    }
  }
}
</script>
